import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { getPreparedZones, groupedTimezones, matchesTimezoneQuery, timezoneDisplayLabel } from '../lib/timezone';

/**
 * TimezoneSelect — a searchable, region-grouped combobox of curated IANA
 * zones (Epic #532 / issue #535 foundation; rebuilt for issue #578).
 *
 * Pre-auth safe: neither this component nor ui/popover, ui/command, or
 * lib/timezone touches AuthContext, axios, or any session — Onboarding and
 * PublicBooking render it logged out.
 *
 * The value is the exact IANA string ('UTC', 'Asia/Manila', …) — every
 * consumer persists this string verbatim; defaults are NOT unified here.
 * The rendered "(GMT+8) Manila (PHT)" label is decorative: never persisted,
 * never an onChange payload. A stale offset after a DST crossing while a tab
 * stays open is an accepted trade (see lib/timezone.js).
 *
 * Duplication note: this is the repo's THIRD Command+Popover combobox
 * (CompanyPicker and phone-input's CountrySelect are the other two). Their
 * contracts are materially different (async fetch / react-phone-number-input
 * shape / static grouped list), so no generic was extracted; three instances
 * is the trigger point for the NEXT author to extract ui/combobox.jsx.
 *
 * Props:
 *  - value: string — the selected IANA zone ('' = unselected)
 *  - onChange: (ianaString) => void — never receives the display label
 *  - id: string — on the trigger button so an external <label htmlFor> works
 *  - placeholder: string — trigger text while value is ''
 *  - disabled: boolean
 *  - className: string — extra classes on the wrapper
 *  - zones: array — override the curated list ({value,label} entries OK;
 *    derived fields are attached on demand for those)
 *  - ariaLabel: string — aria-label for the trigger (PronounsPicker pattern)
 */
export default function TimezoneSelect({
    value,
    onChange,
    id,
    placeholder,
    disabled,
    className = '',
    zones,
    ariaLabel,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const listRef = useRef(null);

    // Lazy + memoised: offsets are computed on FIRST open (getPreparedZones),
    // so pre-auth first paint never pays the 176-zone Intl pass. An explicit
    // `zones` override skips the prepared cache entirely.
    const allZones = useMemo(
        () => (zones ? zones.map(attachDerived) : getPreparedZones()),
        [zones]
    );

    const filtered = useMemo(
        () => allZones.filter((tz) => matchesTimezoneQuery(tz, query)),
        [allZones, query]
    );
    const groups = useMemo(() => groupedTimezones(filtered), [filtered]);
    const noMatches = groups.every((g) => g.zones.length === 0);

    const selected = allZones.find((tz) => tz.value === value);
    const triggerLabel = selected
        ? timezoneDisplayLabel(selected)
        : (placeholder || value || 'Select timezone');

    const handleOpenChange = (next) => {
        setOpen(next);
        if (next) setQuery('');
    };

    // Scroll the selected item into view on open (phone-input.jsx pattern:
    // rAF + [data-selected="true"] query once the panel content is mounted).
    useEffect(() => {
        if (!open) return;
        requestAnimationFrame(() => {
            const list = listRef.current;
            if (!list) return;
            const selectedEl = list.querySelector('[data-selected="true"]');
            if (!selectedEl) return;
            const listRect = list.getBoundingClientRect();
            const itemRect = selectedEl.getBoundingClientRect();
            list.scrollTop += itemRect.top - listRect.top;
        });
    }, [open]);

    const handleSelect = (zoneValue) => {
        if (onChange) onChange(zoneValue);
        setOpen(false);
    };

    return (
        <div className={`w-full ${className}`.trim()}>
            {/*
             * Keep the picker modal when it is opened from a Dialog. Radix
             * otherwise renders the portal as a non-modal layer outside the
             * dialog's scroll-lock/focus boundary, which prevents wheel/touch
             * scrolling in the CommandList on meeting and booking modals.
             */}
            <Popover open={open} onOpenChange={handleOpenChange} modal>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        role="combobox"
                        aria-expanded={open}
                        aria-haspopup="listbox"
                        id={id}
                        aria-label={ariaLabel}
                        disabled={disabled}
                        data-testid="timezone-select"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    >
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                            <Globe className="w-4 h-4 shrink-0 opacity-40" aria-hidden="true" />
                            <span className="truncate">{triggerLabel}</span>
                        </span>
                        <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" aria-hidden="true" />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            value={query}
                            onValueChange={setQuery}
                            placeholder="Search city, country, region, or offset (e.g. +8)…"
                        />
                        <CommandList ref={listRef}>
                            {noMatches && <CommandEmpty>No timezone matches “{query}”.</CommandEmpty>}
                            {groups.map(({ region, zones: groupZones }) => (
                                groupZones.length === 0 ? null : (
                                    <CommandGroup key={region} heading={region}>
                                        {groupZones.map((tz) => (
                                            <CommandItem
                                                key={tz.value}
                                                value={tz.value}
                                                data-selected={tz.value === value}
                                                data-testid={`timezone-option-${tz.value}`}
                                                onSelect={handleSelect}
                                            >
                                                <Check className={`w-4 h-4 mr-2 ${tz.value === value ? 'opacity-100' : 'opacity-0'}`} />
                                                <span>{timezoneDisplayLabel(tz)}</span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )
                            ))}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}

// A bare {value,label} override entry (TimezoneSelect.test.jsx) still renders
// and matches: derived fields are attached on demand and never throw.
function attachDerived(tz) {
    if (tz.offsetMinutes !== undefined) return tz;
    return {
        ...tz,
        region: tz.region || '',
    };
}
